var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { isUndefined } from 'lodash';
import { Close } from '@material-ui/icons';
import { IconButton, Dialog, Divider, withStyles } from '@material-ui/core';
import { Paper } from '@mui/material';
import Resizable from '../Resizable';
import { DIALOG_DEFAULT_SIZE, MIN_DIALOG_SIZE } from '../../constants';
import styles from './styles';
const PaperComponent = (_a) => {
    var { paperRef } = _a, other = __rest(_a, ["paperRef"]);
    return _jsx(Paper, Object.assign({ ref: paperRef }, other));
};
PaperComponent.propTypes = {
    paperRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};
const ResizableDialog = ({ classes, header, content, open, onClose, minDialogSize, defaultDialogSize, defaultPosition, allowBackgroundInteraction, }) => {
    const dialogPaperRef = useRef();
    const [{ width, height }, setSize] = useState(defaultDialogSize);
    const [{ x, y }, setPosition] = useState({ x: defaultPosition.x, y: defaultPosition.y });
    const onResize = useCallback((dimensions) => {
        const { width: newWidth, height: newHeight } = dimensions;
        const widthLeft = window.innerWidth - (x || (window.innerWidth - newWidth) / 2);
        const heightLeft = window.innerHeight - (y || (window.innerHeight - newHeight) / 2);
        setSize({
            width: Math.min(Math.max(minDialogSize.width, newWidth), widthLeft),
            height: Math.min(Math.max(minDialogSize.height, newHeight), heightLeft),
        });
    }, [x, y, minDialogSize]);
    const onDrag = useCallback((coordinates) => {
        const { x: newX, y: newY } = coordinates;
        const widthLeft = window.innerWidth - width;
        // use actual (client) height of paper because maxHeight rule affects height rule
        const heightLeft = window.innerHeight - dialogPaperRef.current.clientHeight;
        setPosition({
            x: Math.min(Math.max(0, newX), widthLeft),
            y: Math.min(Math.max(0, newY), heightLeft),
        });
    }, [width]);
    const backgroundInteractionProps = {
        disableEnforceFocus: allowBackgroundInteraction,
        style: { position: allowBackgroundInteraction ? 'initial' : 'fixed' },
    };
    return (_jsx(Dialog, Object.assign({}, backgroundInteractionProps, { open: open, onClose: onClose, BackdropProps: {
            className: classes.backdrop,
        }, PaperProps: {
            component: PaperComponent,
            paperRef: dialogPaperRef,
            className: classes.paper,
            style: {
                top: isUndefined(y) ? '50%' : y,
                left: isUndefined(x) ? '50%' : x,
                transform: `translate(${isUndefined(x) ? '-50%' : 0}, ${isUndefined(y) ? '-50%' : 0})`,
                width,
                height,
                zIndex: 1200,
            },
        }, fullWidth: true, children: _jsxs(Resizable, { width: width, height: height, onResize: onResize, onDrag: onDrag, children: [_jsxs("div", { className: classes.header, children: [header, _jsx(IconButton, { className: classes.button, onClick: onClose, title: 'Close', children: _jsx(Close, {}) })] }), _jsx(Divider, {}), _jsx("div", { className: classes.content, children: content })] }) })));
};
ResizableDialog.defaultProps = {
    minDialogSize: MIN_DIALOG_SIZE,
    defaultDialogSize: DIALOG_DEFAULT_SIZE,
    defaultPosition: { x: undefined, y: undefined },
};
ResizableDialog.propTypes = {
    classes: PropTypes.shape({
        paper: PropTypes.string.isRequired,
        backdrop: PropTypes.string.isRequired,
        header: PropTypes.string.isRequired,
        button: PropTypes.string.isRequired,
        content: PropTypes.string.isRequired,
    }).isRequired,
    minDialogSize: PropTypes.shape({
        height: PropTypes.number.isRequired,
        width: PropTypes.number.isRequired,
    }),
    defaultDialogSize: PropTypes.shape({
        height: PropTypes.number.isRequired,
        width: PropTypes.number.isRequired,
    }),
    defaultPosition: PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number,
    }),
    open: PropTypes.bool.isRequired,
    content: PropTypes.element.isRequired,
    header: PropTypes.element.isRequired,
    onClose: PropTypes.func.isRequired,
    allowBackgroundInteraction: PropTypes.bool,
};
export default memo(withStyles(styles)(ResizableDialog));
